
import { useCallback, useEffect, useRef, useState } from "react";
import { getOverlay, putOverlay } from "@/lib/api/endpoints";
import {
  emptyOverlay,
  type Profile,
  type WorkbookOverlay,
} from "@/lib/mapper/types";

export const OVERLAY_SAVE_DEBOUNCE_MS = 600;

type PersistOverlay = (
  workbookId: string,
  sheet: string,
  overlay: WorkbookOverlay,
) => Promise<WorkbookOverlay>;

type OverlayControllerOptions = {
  workbookId: string;
  sheet: string;
  initialOverlay?: WorkbookOverlay;
  debounceMs?: number;
  persist?: PersistOverlay;
  onOverlayChange?: (overlay: WorkbookOverlay) => void;
  onSavingChange?: (saving: boolean) => void;
};

export type OverlaySaveController = {
  getSnapshot: () => WorkbookOverlay;
  setSnapshot: (overlay: WorkbookOverlay) => void;
  setOverride: (rowId: string, column: string, value: string) => void;
  resetCell: (rowId: string, column: string) => void;
  addRow: (rowOrderFallback?: string[]) => string;
  addColumn: (name: string) => void;
  removeRows: (rowIds: string[], rowOrderFallback?: string[]) => void;
  /** Store AI output (base row id → column → text); saved right away. */
  mergeGenerated: (cells: Record<string, Record<string, string>>) => void;
  /** Forget stored AI output so those cells are "pending" again. */
  clearGenerated: (columns: string[], rowIds?: string[]) => void;
  /** Save (or with null, drop) the sheet's own rules. */
  setProfile: (profile: Profile | null) => void;
  /** Persist now instead of waiting for the debounce. */
  flush: () => Promise<void>;
  hasLocalChanges: () => boolean;
  dispose: () => void;
};

/**
 * One source row can expand into several grid rows (variants, image rows);
 * the preview ids those `<baseRowId>#<n>`. Product-level things — row order,
 * adding/removing products, stored AI output — are keyed by the base id.
 */
export function baseRowId(rowId: string): string {
  const hash = rowId.lastIndexOf("#");
  return hash > 0 && /^\d+$/.test(rowId.slice(hash + 1))
    ? rowId.slice(0, hash)
    : rowId;
}

function uniqueBaseRowIds(rowIds: readonly string[]): string[] {
  return [...new Set(rowIds.map(baseRowId))];
}

export function makeOverlayRowId(): string {
  if (typeof globalThis.crypto?.randomUUID === "function") {
    return globalThis.crypto.randomUUID();
  }
  return `row-${Math.random().toString(36).slice(2, 10)}`;
}

export function withCellOverride(
  overlay: WorkbookOverlay,
  rowId: string,
  column: string,
  value: string,
): WorkbookOverlay {
  return {
    ...overlay,
    overrides: {
      ...overlay.overrides,
      [rowId]: {
        ...(overlay.overrides[rowId] ?? {}),
        [column]: value,
      },
    },
  };
}

export function withoutCellOverride(
  overlay: WorkbookOverlay,
  rowId: string,
  column: string,
): WorkbookOverlay {
  const currentRow = overlay.overrides[rowId];
  if (!currentRow || !Object.prototype.hasOwnProperty.call(currentRow, column)) {
    return overlay;
  }

  const nextRow = { ...currentRow };
  delete nextRow[column];

  const nextOverrides = { ...overlay.overrides };
  if (Object.keys(nextRow).length > 0) {
    nextOverrides[rowId] = nextRow;
  } else {
    delete nextOverrides[rowId];
  }

  return {
    ...overlay,
    overrides: nextOverrides,
  };
}

export function withAddedRow(
  overlay: WorkbookOverlay,
  rowId: string = makeOverlayRowId(),
  rowOrderFallback?: string[],
): WorkbookOverlay {
  const baseOrder =
    Array.isArray(rowOrderFallback) && rowOrderFallback.length > 0
      ? uniqueBaseRowIds(rowOrderFallback)
      : overlay.rowOrder;
  const nextOrder = baseOrder.includes(rowId) ? baseOrder : [...baseOrder, rowId];

  return {
    ...overlay,
    rowOrder: nextOrder,
    addedRows: {
      ...overlay.addedRows,
      [rowId]: overlay.addedRows[rowId] ?? {},
    },
  };
}

export function withAddedColumn(
  overlay: WorkbookOverlay,
  name: string,
): WorkbookOverlay {
  const normalizedName = name.trim();
  if (!normalizedName) return overlay;
  if (overlay.addedColumns.some((column) => column.name === normalizedName)) {
    return overlay;
  }

  return {
    ...overlay,
    addedColumns: [
      ...overlay.addedColumns,
      { name: normalizedName, kind: "manual" },
    ],
  };
}

export function withoutRows(
  overlay: WorkbookOverlay,
  rowIds: string[],
  rowOrderFallback?: string[],
): WorkbookOverlay {
  // Removing any grid row of a product removes the whole product.
  const remove = new Set(
    uniqueBaseRowIds(rowIds.filter((rowId) => rowId.length > 0)),
  );
  if (remove.size === 0) return overlay;

  const baseOrder =
    Array.isArray(rowOrderFallback) && rowOrderFallback.length > 0
      ? uniqueBaseRowIds(rowOrderFallback)
      : overlay.rowOrder;
  const addedRows = { ...overlay.addedRows };
  const overrides = { ...overlay.overrides };
  const generated = { ...(overlay.generated ?? {}) };

  for (const rowId of remove) {
    delete addedRows[rowId];
    delete overrides[rowId];
    delete generated[rowId];
  }
  for (const rowId of Object.keys(overrides)) {
    if (remove.has(baseRowId(rowId))) delete overrides[rowId];
  }

  return withGeneratedCells(
    {
      ...overlay,
      rowOrder: baseOrder.filter((rowId) => !remove.has(rowId)),
      addedRows,
      overrides,
    },
    generated,
  );
}

export function withMergedGenerated(
  overlay: WorkbookOverlay,
  cells: Record<string, Record<string, string>>,
): WorkbookOverlay {
  let changed = false;
  const generated = { ...(overlay.generated ?? {}) };
  for (const [rowId, row] of Object.entries(cells ?? {})) {
    const base = baseRowId(rowId);
    const merged = { ...(generated[base] ?? {}) };
    for (const [column, value] of Object.entries(row ?? {})) {
      if (typeof value !== "string" || value === "") continue;
      if (merged[column] === value) continue;
      merged[column] = value;
      changed = true;
    }
    generated[base] = merged;
  }
  return changed ? withGeneratedCells(overlay, generated) : overlay;
}

export function withoutGenerated(
  overlay: WorkbookOverlay,
  columns: readonly string[],
  rowIds?: readonly string[],
): WorkbookOverlay {
  if (!overlay.generated) return overlay;
  const drop = new Set(columns);
  const only = rowIds ? new Set(uniqueBaseRowIds(rowIds)) : null;
  let changed = false;
  const generated: Record<string, Record<string, string>> = {};
  for (const [rowId, row] of Object.entries(overlay.generated)) {
    if (only && !only.has(rowId)) {
      generated[rowId] = row;
      continue;
    }
    const kept: Record<string, string> = {};
    for (const [column, value] of Object.entries(row)) {
      if (drop.has(column)) changed = true;
      else kept[column] = value;
    }
    generated[rowId] = kept;
  }
  return changed ? withGeneratedCells(overlay, generated) : overlay;
}

function withGeneratedCells(
  overlay: WorkbookOverlay,
  generated: Record<string, Record<string, string>>,
): WorkbookOverlay {
  const cleaned: Record<string, Record<string, string>> = {};
  for (const [rowId, row] of Object.entries(generated)) {
    if (Object.keys(row).length > 0) cleaned[rowId] = row;
  }
  const next = { ...overlay };
  if (Object.keys(cleaned).length > 0) next.generated = cleaned;
  else delete next.generated;
  return next;
}

export function withProfile(
  overlay: WorkbookOverlay,
  profile: Profile | null,
): WorkbookOverlay {
  const next = { ...overlay };
  if (profile) next.profile = profile;
  else delete next.profile;
  return next;
}

export function createOverlaySaveController({
  workbookId,
  sheet,
  initialOverlay = emptyOverlay(),
  debounceMs = OVERLAY_SAVE_DEBOUNCE_MS,
  persist = putOverlay,
  onOverlayChange,
  onSavingChange,
}: OverlayControllerOptions): OverlaySaveController {
  let overlay = initialOverlay;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let inFlight = 0;
  let revision = 0;
  let disposed = false;

  function emitSaving() {
    onSavingChange?.(timer !== null || inFlight > 0);
  }

  function saveNow(): Promise<void> {
    if (disposed) return Promise.resolve();
    if (timer) clearTimeout(timer);
    timer = null;
    const snapshot = overlay;
    const snapshotRevision = revision;

    inFlight += 1;
    emitSaving();

    return persist(workbookId, sheet, snapshot)
      .then((saved) => {
        if (disposed) return;
        if (overlay === snapshot && revision === snapshotRevision) {
          overlay = saved;
          revision = 0;
          onOverlayChange?.(saved);
        }
      })
      .finally(() => {
        inFlight = Math.max(0, inFlight - 1);
        if (!disposed) emitSaving();
      });
  }

  function scheduleSave(delay = debounceMs) {
    if (disposed) return;
    if (timer) clearTimeout(timer);

    timer = setTimeout(() => {
      // Background autosave: failures are retried by the next change.
      saveNow().catch((err) => {
        console.error("[workbook overlay] save failed", err);
      });
    }, delay);

    emitSaving();
  }

  function commit(next: WorkbookOverlay, delay?: number) {
    if (next === overlay) return;
    overlay = next;
    revision += 1;
    onOverlayChange?.(next);
    scheduleSave(delay);
  }

  return {
    getSnapshot: () => overlay,
    setSnapshot(next) {
      overlay = next;
      revision = 0;
      onOverlayChange?.(next);
    },
    setOverride(rowId, column, value) {
      commit(withCellOverride(overlay, rowId, column, value));
    },
    resetCell(rowId, column) {
      commit(withoutCellOverride(overlay, rowId, column));
    },
    addRow(rowOrderFallback) {
      const rowId = makeOverlayRowId();
      commit(withAddedRow(overlay, rowId, rowOrderFallback));
      return rowId;
    },
    addColumn(name) {
      commit(withAddedColumn(overlay, name));
    },
    removeRows(rowIds, rowOrderFallback) {
      commit(withoutRows(overlay, rowIds, rowOrderFallback));
    },
    // AI output costs money to regenerate: save it without the edit debounce.
    mergeGenerated(cells) {
      commit(withMergedGenerated(overlay, cells), 0);
    },
    clearGenerated(columns, rowIds) {
      commit(withoutGenerated(overlay, columns, rowIds));
    },
    setProfile(profile) {
      commit(withProfile(overlay, profile));
    },
    flush() {
      if (timer === null && revision === 0) return Promise.resolve();
      return saveNow();
    },
    hasLocalChanges: () => revision > 0,
    dispose() {
      disposed = true;
      if (timer) clearTimeout(timer);
      timer = null;
      inFlight = 0;
      emitSaving();
    },
  };
}

export function useOverlay(workbookId: string, sheet: string) {
  const [overlay, setOverlay] = useState<WorkbookOverlay>(() => emptyOverlay());
  const [saving, setSaving] = useState(false);
  // True once the saved overlay has been fetched (or the fetch failed), so the
  // page can pick the sheet's own saved rules before its first preview.
  const [ready, setReady] = useState(false);
  const controllerRef = useRef<OverlaySaveController | null>(null);

  useEffect(() => {
    let cancelled = false;
    const initial = emptyOverlay();
    const controller = createOverlaySaveController({
      workbookId,
      sheet,
      initialOverlay: initial,
      onOverlayChange: setOverlay,
      onSavingChange: setSaving,
    });

    controllerRef.current = controller;
    setOverlay(initial);
    setSaving(false);
    setReady(false);

    getOverlay(workbookId, sheet)
      .then((loaded) => {
        if (cancelled || controller.hasLocalChanges()) return;
        controller.setSnapshot(loaded ?? emptyOverlay());
      })
      .catch((err) => {
        console.error("[workbook overlay] load failed", err);
      })
      .finally(() => {
        if (!cancelled) setReady(true);
      });

    return () => {
      cancelled = true;
      controller.dispose();
      if (controllerRef.current === controller) {
        controllerRef.current = null;
      }
    };
  }, [workbookId, sheet]);

  const setOverride = useCallback(
    (rowId: string, column: string, value: string) => {
      controllerRef.current?.setOverride(rowId, column, value);
    },
    [],
  );

  const resetCell = useCallback((rowId: string, column: string) => {
    controllerRef.current?.resetCell(rowId, column);
  }, []);

  const addRow = useCallback((rowOrderFallback?: string[]) => {
    return controllerRef.current?.addRow(rowOrderFallback);
  }, []);

  const addColumn = useCallback((name: string) => {
    controllerRef.current?.addColumn(name);
  }, []);

  const removeRows = useCallback(
    (rowIds: string[], rowOrderFallback?: string[]) => {
      controllerRef.current?.removeRows(rowIds, rowOrderFallback);
    },
    [],
  );

  const mergeGenerated = useCallback(
    (cells: Record<string, Record<string, string>>) => {
      controllerRef.current?.mergeGenerated(cells);
    },
    [],
  );

  const clearGenerated = useCallback((columns: string[], rowIds?: string[]) => {
    controllerRef.current?.clearGenerated(columns, rowIds);
  }, []);

  const setSheetProfile = useCallback((profile: Profile | null) => {
    controllerRef.current?.setProfile(profile);
  }, []);

  const flush = useCallback(
    () => controllerRef.current?.flush() ?? Promise.resolve(),
    [],
  );

  return {
    overlay,
    setOverride,
    resetCell,
    addRow,
    addColumn,
    removeRows,
    mergeGenerated,
    clearGenerated,
    setSheetProfile,
    flush,
    saving,
    ready,
  };
}
